import { supabase } from '../supabaseClient';
import { applyLiveDocumentFilter } from './liveDocuments';
import {
  computeDocumentState,
  DOCUMENT_STATES,
  formatDateBritish,
} from './compliance';

// Enumerate the compliance documents currently BLOCKING a supplier
// (outstanding or expired), reusing the same computeDocumentState the
// supplier's own /compliance page runs (src/lib/compliance.js) so a reason
// shown on the compare screen reads the same as the reason the supplier
// sees on their portal — one derivation, not a fresh one.
//
// The supplier's headline green/amber/red state is NOT recomputed here:
// callers take that from the trusted suppliers.compliance_status column
// (the value SupplierDetail and the compliance dashboard render, kept in
// lockstep by the DB trigger). This helper only explains a red — the
// "why" line and the fix link.
//
// Scope note: upload-type docs only. Acknowledge-type blocking docs are
// rare and aren't enumerated here; the trusted column still accounts for
// them, so the chip stays correct and callers fall back to a generic
// "view details" when a red supplier yields no enumerated reasons. The
// requiredKeys derivation mirrors useSupplierCompliance.loadRequiredKeys:
// tier requirements ∪ per-supplier overrides − not_required, intersected
// with live (non-draft, active) master docs.
export async function fetchBlockingComplianceDocs(supplierId, riskFlag) {
  if (!supplierId) return [];

  const [tierReq, recordsRes, masterRes] = await Promise.all([
    riskFlag
      ? supabase.from('risk_document_requirements').select('document_key').eq('risk_level', riskFlag)
      : Promise.resolve({ data: [] }),
    supabase
      .from('supplier_onboarding_documents')
      .select('document_key, expiry_date, expiry_approved_at, not_required, risk_override_applicable, supplier_uploaded_at, signed_at, review_decision')
      .eq('supplier_id', supplierId),
    applyLiveDocumentFilter(supabase
      .from('fusion_documents')
      .select('compliance_doc_key, document_name')
      .not('compliance_doc_key', 'is', null)),
  ]);

  const records = recordsRes.data || [];

  // requiredKeys = tier requirements ∪ per-supplier overrides − not_required.
  const required = new Set((tierReq.data || []).map(r => r.document_key));
  for (const r of records) if (r.risk_override_applicable) required.add(r.document_key);
  for (const r of records) if (r.not_required) required.delete(r.document_key);

  const labelByKey = new Map((masterRes.data || []).map(d => [d.compliance_doc_key, d.document_name]));
  const recByKey = new Map(records.map(r => [r.document_key, r]));

  const blocking = [];
  for (const key of required) {
    // Only count keys backed by a live master doc — a tier requirement
    // pointing at a draft/removed doc is a phantom and shouldn't surface.
    if (!labelByKey.has(key)) continue;
    const state = computeDocumentState(recByKey.get(key) || null);
    if (state !== DOCUMENT_STATES.OUTSTANDING && state !== DOCUMENT_STATES.EXPIRED) continue;
    const rec = recByKey.get(key);
    const label = labelByKey.get(key) || key;
    const detail = (state === DOCUMENT_STATES.EXPIRED && rec?.expiry_date)
      ? `${label} expired ${formatDateBritish(rec.expiry_date)}`
      : `${label} outstanding`;
    blocking.push({ key, label, state, detail });
  }

  // Expired first (most urgent), then outstanding; alphabetical within.
  blocking.sort((a, b) => {
    if (a.state !== b.state) return a.state === DOCUMENT_STATES.EXPIRED ? -1 : 1;
    return a.label.localeCompare(b.label);
  });
  return blocking;
}
